import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '../../../Box';
import Divider from '../../../Divider';
import { MenuVertical } from '../../../Icons/Core';
import { CrossFade } from '../../../CrossFade/CrossFade';
import { NavigationBarRoutesItem } from '../NavigationBarRoutesItem/NavigationBarRoutesItem';
import { NavigationBarRoutes } from '../NavigationBarRoutes/NavigationBarRoutes';
import { StyledNavigationRootContainer, StyledNavigationLogoContainer, StyledProductContainer, StyledGlobalContainer, StyledTopContainer, NavigationSubMenu, ClickCapture } from './NavigationBar.styles';
import { renderSubmenuContent } from './renderSubmenuContext';
const matchLocation = (basePath, pathname) => {
    const pattern = new RegExp(`^${basePath}($|[/#?])`);
    return !!pattern.test(pathname);
};
export const NavigationBar = props => {
    var _a, _b, _c, _d;
    const { routes = [], topOptions = [], options = [], logo, currentPath, onNavigate } = props;
    const anchorRef = useRef(null);
    const theme = useTheme();
    const [submenuState, setSubmenuState] = useState({
        open: false
    });
    const productContainerRef = useRef(null);
    const globalContainerRef = useRef(null);
    const topContainerRef = useRef(null);
    const logoContainerRef = useRef(null);
    const [overflowed, setOverflowed] = useState(false);
    const [visibleRoutesCount, setVisibleRoutesCount] = useState(0); // Track the number of visible routes
    const additionalPadding = 100;
    const checkOverflow = () => {
        var _a, _b, _c;
        // Ensure each ref has a value; if any ref is missing, we treat it as 0
        const anchorHeight = ((_a = anchorRef.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0;
        const topContainerHeight = ((_b = topContainerRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
        const globalContainerHeight = ((_c = globalContainerRef.current) === null || _c === void 0 ? void 0 : _c.clientHeight) || 0;
        // Calculate the total available height
        const totalHeight = anchorHeight - topContainerHeight - globalContainerHeight - additionalPadding;
        // Check how many routes can fit in the remaining space
        const routeHeight = 50;
        const maxVisibleItems = Math.floor(totalHeight / routeHeight);
        setVisibleRoutesCount(maxVisibleItems);
        // Check if we have overflow
        const isOverflowing = routes.length > maxVisibleItems;
        setOverflowed(isOverflowing);
    };
    // Initial setup and window resizing handling
    useEffect(() => {
        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(() => {
                checkOverflow(); // Recalculate overflow when resizing
            });
            // Start observing the anchor container
            if (anchorRef.current) {
                resizeObserver.observe(anchorRef.current);
            }
            // Initial check
            checkOverflow();
            return () => {
                // Clean up
                if (anchorRef.current) {
                    resizeObserver.unobserve(anchorRef.current);
                }
                resizeObserver.disconnect();
            };
        }
        else {
            // Fallback for environments without ResizeObserver
            window.addEventListener('resize', checkOverflow);
            checkOverflow(); // Initial check on mount
            return () => {
                window.removeEventListener('resize', checkOverflow);
            };
        }
    }, [routes]);
    // Function to find the active route index based on the current path and type
    const findActiveIndex = (routes, currentPath) => {
        const checkRoute = (route) => {
            if (route.path && matchLocation(route.path, currentPath)) {
                return true;
            }
            if (route.children && Array.isArray(route.children)) {
                for (const child of route.children) {
                    if (checkRoute(child)) {
                        return true;
                    }
                }
            }
            return false;
        };
        return routes.findIndex(route => checkRoute(route));
    };
    // Get the active route index for both top options and regular routes
    const activeRouteIndex = findActiveIndex(routes, currentPath);
    // Handle submenu for route or option
    const setRouteSubmenu = useCallback((index, type) => {
        setSubmenuState(currentState => ({
            open: true,
            active: {
                index,
                type
            },
            last: currentState.active
        }));
    }, [setSubmenuState]);
    const setOverflowedSubmenu = useCallback(() => {
        setSubmenuState(currentState => ({
            open: true,
            active: {
                index: routes.length,
                type: 'overflowed'
            },
            last: currentState.active
        }));
    }, [setSubmenuState]);
    const setOptionSubmenu = useCallback((index, type) => {
        setSubmenuState(currentState => ({
            open: true,
            active: {
                index,
                type
            },
            last: currentState.active
        }));
    }, [setSubmenuState]);
    const setSubmenuClosed = useCallback(() => {
        setSubmenuState({
            open: false,
            active: undefined,
            last: undefined
        });
    }, [setSubmenuState]);
    useEffect(() => {
        setSubmenuClosed();
    }, [currentPath]);
    const processedOptions = useMemo(() => options.map(option => (Object.assign(Object.assign({}, option), { icon: 'icon' in option ? option.icon : undefined, initials: 'initials' in option ? option.initials : undefined }))), [options]);
    const handleClick = useCallback((index, type) => {
        setOptionSubmenu(index, type);
    }, []);
    /* Set isMenuOpen based on submenuState */
    const isMenuOpen = submenuState.open;
    const isCurrentPathInVisibleRoutes = routes
        .slice(0, visibleRoutesCount)
        .some(route => { var _a; return matchLocation((_a = route.path) !== null && _a !== void 0 ? _a : '', currentPath); });
    return (React.createElement(StyledNavigationRootContainer, { ref: anchorRef, "data-testid": "navigation-bar-root-container" },
        React.createElement(Box, { display: "flex", flexDirection: "row", sx: { height: '100%' } },
            React.createElement(Box, { display: "flex", flexDirection: "column", sx: { height: '100%' } },
                React.createElement(StyledNavigationLogoContainer, { ref: logoContainerRef, flexDirection: "column", "data-testid": "navigation-bar-logo-container" }, React.cloneElement(logo, {
                    width: theme.palette.icon.size.xlarge,
                    height: theme.palette.icon.size.xlarge,
                    viewBox: '0 0 56 56' // TODO: Investigate why this is needed
                })),
                topOptions.length > 0 && (React.createElement(React.Fragment, null,
                    React.createElement(StyledTopContainer, { ref: topContainerRef, "data-testid": "navigation-bar-top-container" }, topOptions.map((option, index) => {
                        var _a, _b, _c, _d;
                        return (React.createElement(NavigationBarRoutesItem, { label: option.name, key: option.name, onClick: () => {
                                // Only set the submenu state if the option has a menu
                                if (option.menu) {
                                    handleClick(index, 'topOption');
                                }
                                // If the user passes in an onClick (option.onClick exists), call that as well
                                if (option.onClick) {
                                    option.onClick();
                                }
                                if (option.path) {
                                    onNavigate(option.path);
                                }
                            }, active: !!((((_a = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _a === void 0 ? void 0 : _a.type) === 'topOption' && index === ((_b = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _b === void 0 ? void 0 : _b.index)) ||
                                (option.path && matchLocation(option.path, currentPath))), disabled: option.disabled, focused: !!((((_c = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _c === void 0 ? void 0 : _c.type) === 'topOption' && index === ((_d = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _d === void 0 ? void 0 : _d.index)) ||
                                (option.path && matchLocation(option.path, currentPath))), icon: option.icon, isMenuOpen: isMenuOpen, href: option.href, path: option.path, openInNewTab: option.openInNewTab }));
                    })),
                    React.createElement(Divider, null),
                    React.createElement(Box, { sx: { paddingBottom: theme.palette.space.container.dense } }))),
                React.createElement(StyledProductContainer, { ref: productContainerRef, display: "flex", flexDirection: "column", "data-testid": "navigation-bar-product-container" },
                    React.createElement(NavigationBarRoutes, { routes: routes.slice(0, visibleRoutesCount), setSubmenuRoute: (route, index) => {
                            if (route === null || route === void 0 ? void 0 : route.children) {
                                setRouteSubmenu(index, 'route');
                            }
                            else {
                                setSubmenuClosed();
                            }
                        }, activeIndexes: [activeRouteIndex], focusedIndexes: [(((_a = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _a === void 0 ? void 0 : _a.type) === 'route' && ((_b = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _b === void 0 ? void 0 : _b.index)) || -1], onNavigate: onNavigate, isMenuOpen: isMenuOpen }),
                    overflowed && (React.createElement(NavigationBarRoutesItem, { label: "More", icon: React.createElement(MenuVertical, null), onClick: setOverflowedSubmenu, active: ((_c = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _c === void 0 ? void 0 : _c.type) === 'overflowed' || !isCurrentPathInVisibleRoutes, focused: ((_d = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _d === void 0 ? void 0 : _d.type) === 'overflowed', isMenuOpen: isMenuOpen }))),
                React.createElement(StyledGlobalContainer, { ref: globalContainerRef, "data-testid": "navigation-bar-global-container" }, processedOptions.map((option, index) => {
                    var _a, _b, _c, _d;
                    return (React.createElement(NavigationBarRoutesItem, { label: option.name, key: option.name, onClick: () => {
                            handleClick(index, 'option');
                            // If the user passing in an onClick (option.onClick exists), call that as well
                            if (option.onClick) {
                                option.onClick();
                            }
                        }, active: ((_a = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _a === void 0 ? void 0 : _a.type) === 'option' && index === ((_b = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _b === void 0 ? void 0 : _b.index), disabled: option.disabled, focused: ((_c = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _c === void 0 ? void 0 : _c.type) === 'option' && index === ((_d = submenuState === null || submenuState === void 0 ? void 0 : submenuState.active) === null || _d === void 0 ? void 0 : _d.index), icon: option.icon, initials: option.initials, isMenuOpen: isMenuOpen }));
                }))),
            React.createElement(Box, { display: "flex" },
                React.createElement(NavigationSubMenu, { open: submenuState.open, "data-testid": "navigation-bar-submenu" }, submenuState.active && (React.createElement(CrossFade, null, renderSubmenuContent({
                    submenuState,
                    routes,
                    topOptions,
                    options,
                    visibleRoutesCount,
                    currentPath,
                    onNavigate,
                    setSubmenuClosed
                }))))),
            submenuState.open && React.createElement(ClickCapture, { role: "presentation", onClick: setSubmenuClosed }))));
};
//# sourceMappingURL=NavigationBar.js.map